import { Button, Form } from 'shared/components/ui';
import React, { useState, useCallback } from 'react';

import './EmailInput.css';

// Dominios comunes para detectar typos
const DOMINIOS_COMUNES = [
  'gmail.com', 'hotmail.com', 'yahoo.com', 'outlook.com', 'live.com',
  'hotmail.com.ar', 'yahoo.com.ar', 'outlook.com.ar',
  'fibertel.com.ar', 'speedy.com.ar', 'arnet.com.ar', 'ciudad.com.ar',
  'telecentro.com.ar', 'icloud.com', 'protonmail.com', 'mail.com'
];

// Distancia de Levenshtein simplificada
function distancia(a, b) {
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b[i - 1] === a[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  return matrix[b.length][a.length];
}

function buscarSugerencia(email) {
  const partes = email.split('@');
  if (partes.length !== 2) return null;

  const dominio = partes[1].toLowerCase();
  
  // Si el dominio es exacto, no sugerir nada
  if (DOMINIOS_COMUNES.includes(dominio)) return null;

  // Buscar el dominio más cercano
  let mejorDistancia = Infinity;
  let mejorDominio = null;

  for (const d of DOMINIOS_COMUNES) {
    const dist = distancia(dominio, d);
    // Solo sugerir si la diferencia es de 1-2 caracteres y el largo es similar
    if (dist > 0 && dist <= 2 && Math.abs(dominio.length - d.length) <= 2) {
      if (dist < mejorDistancia) {
        mejorDistancia = dist;
        mejorDominio = d;
      }
    }
  }

  if (mejorDominio) {
    return `${partes[0]}@${mejorDominio}`;
  }
  return null;
}

const EmailInput = ({ value, onChange, name = 'correo', disabled = false, placeholder = '', onValidationChange }) => {
  const [sugerencia, setSugerencia] = useState(null);
  const [error, setError] = useState('');

  const validarEmail = useCallback((email) => {
    if (!email) {
      setSugerencia(null);
      setError('');
      onValidationChange?.(true);
      return;
    }

    // Validación de formato estricta
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setError('Formato de email inválido');
      setSugerencia(null);
      onValidationChange?.(false);
      return;
    }

    // Verificar puntos consecutivos
    if (email.includes('..')) {
      setError('El email no puede tener puntos consecutivos');
      setSugerencia(null);
      onValidationChange?.(false);
      return;
    }

    // Verificar que el dominio tenga al menos un punto
    const dominio = email.split('@')[1];
    if (!dominio || !dominio.includes('.')) {
      setError('El dominio del email no es válido');
      setSugerencia(null);
      onValidationChange?.(false);
      return;
    }

    setError('');
    onValidationChange?.(true);
    const sug = buscarSugerencia(email);
    setSugerencia(sug);
  }, [onValidationChange]);

  const handleBlur = (e) => {
    validarEmail(e.target.value);
  };

  const handleChange = (e) => {
    setSugerencia(null);
    setError('');
    onValidationChange?.(true);
    // Bloquear caracteres no válidos en emails (incluidos acentos y caracteres especiales)
    const valor = e.target.value.replace(/[^a-zA-Z0-9._@-]/g, '');
    const event = {
      target: { name: e.target.name, value: valor, type: 'text' }
    };
    onChange(event);
  };

  const aceptarSugerencia = () => {
    const event = {
      target: { name, value: sugerencia, type: 'text' }
    };
    onChange(event);
    setSugerencia(null);
  };

  return (
    <div className="email-input-wrapper">
      <Form.Control
        type="text"
        name={name}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={placeholder}
        className={error ? 'is-invalid' : sugerencia ? 'email-warning' : ''}
      />
      {error && (
        <div className="email-error">{error}</div>
      )}
      {sugerencia && (
        <div className="email-sugerencia">
          ¿Quisiste decir <strong>{sugerencia}</strong>?
          <Button type="button" className="email-sugerencia-btn" onClick={aceptarSugerencia}>
            Sí, corregir
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmailInput;
